import React, { useMemo } from "react";
import Select, { type SingleValue } from "react-select";
import type {
  AgendaConsultant,
  AgendaDayPayload,
  AgendaFiltersState,
  AgendaWeekSummary,
} from "../../agendaTypes";
import type { AgendaWeatherAlert } from "../../agendaWeatherTypes";
import { useAgendaWeatherContext } from "../../context/AgendaWeatherContext";
import MobileConsultantAgenda from "./MobileConsultantAgenda";
import MobileTeamSelect from "./MobileTeamSelect";
import { mobileSelectStyles, type MobileSelectOption } from "./mobileSelectStyles";

type MobileAdminAgendaProps = {
  userFirstName: string;
  consultant: AgendaConsultant;
  consultants: AgendaConsultant[];
  cityOptions: string[];
  filters: AgendaFiltersState;
  dayData: AgendaDayPayload;
  weekSummary: AgendaWeekSummary;
  loadingDay: boolean;
  weatherAlerts: AgendaWeatherAlert[];
  onApplyFilters: (patch: Partial<AgendaFiltersState>) => void;
  onOpenVisit: (coord: string, scheduleId?: number) => void;
  onScheduleFreeSlot: (coord: string) => void;
  /** Unlock a blocked slot; omitted when the viewer cannot change this agenda. */
  onUnlockSlot?: (coord: string) => void;
  onBulkBlock: () => void;
  onVisitOutcomeUpdated: () => void;
  detailPanel: React.ReactNode;
};

const MobileAdminAgenda: React.FC<MobileAdminAgendaProps> = ({
  userFirstName,
  consultant,
  consultants,
  cityOptions,
  filters,
  dayData,
  weekSummary,
  loadingDay,
  weatherAlerts,
  onApplyFilters,
  onOpenVisit,
  onScheduleFreeSlot,
  onUnlockSlot,
  onBulkBlock,
  onVisitOutcomeUpdated,
  detailPanel,
}) => {
  const agendaWeather = useAgendaWeatherContext();
  const selectedConsultantId = filters.consultantId ?? consultant.id;
  const selectedCity = filters.city ?? "";
  const consultantWeather =
    agendaWeather.cityByName.get(consultant.city) ??
    agendaWeather.cityByName.get(consultant.city.normalize("NFD").replace(/[\u0300-\u036f]/g, "")) ??
    agendaWeather.primaryCityWeather;

  const controls = useMemo(
    () => (
      <section className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Filtros da agenda">
        <MobileTeamSelect />
        <div className="min-w-0">
          <span className="sr-only">Consultor</span>
          <Select<MobileSelectOption>
            options={consultants.map((item) => ({
              value: item.id,
              label: item.first_name,
            }))}
            value={
              consultants
                .map((item) => ({ value: item.id, label: item.first_name }))
                .find((option) => option.value === selectedConsultantId) ?? null
            }
            onChange={(option: SingleValue<MobileSelectOption>) =>
              onApplyFilters({
                consultantId: option ? Number(option.value) : undefined,
              })
            }
            isSearchable={false}
            styles={mobileSelectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
          />
        </div>
        <div className="min-w-0">
          <span className="sr-only">Cidade</span>
          <Select<MobileSelectOption>
            options={[
              { value: "", label: "Todas as cidades" },
              ...cityOptions.map((city) => ({ value: city, label: city })),
            ]}
            value={
              [{ value: "", label: "Todas as cidades" }, ...cityOptions.map((city) => ({ value: city, label: city }))].find(
                (option) => option.value === selectedCity
              ) ?? { value: "", label: "Todas as cidades" }
            }
            onChange={(option: SingleValue<MobileSelectOption>) =>
              onApplyFilters({ city: option?.value ? String(option.value) : undefined })
            }
            isSearchable={false}
            styles={mobileSelectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
          />
        </div>
      </section>
    ),
    [cityOptions, consultants, onApplyFilters, selectedCity, selectedConsultantId]
  );

  return (
    <MobileConsultantAgenda
      userFirstName={userFirstName}
      consultant={consultant}
      dayData={dayData}
      weekSummary={weekSummary}
      selectedDateIso={filters.date}
      loadingDay={loadingDay}
      canManageSchedules
      weatherAlerts={weatherAlerts}
      onSelectDate={(isoDate) => onApplyFilters({ date: isoDate })}
      onOpenVisit={onOpenVisit}
      onScheduleFreeSlot={onScheduleFreeSlot}
      onUnlockSlot={onUnlockSlot}
      onBulkBlock={onBulkBlock}
      onVisitOutcomeUpdated={onVisitOutcomeUpdated}
      headerTitle="Agenda"
      beforeWeekStrip={controls}
      weatherCityOverride={consultant.city}
      weatherCityWeatherOverride={consultantWeather}
      detailPanel={detailPanel}
    />
  );
};

export default MobileAdminAgenda;
